import React, { PropsWithChildren, ReactElement } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import { styled } from '@mui/material/styles'
import { ErrorOutlined, InfoOutlined, WarningAmber } from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'
import { useTranslate } from 'src/i18n/useMessageSource'

const StyledCloseIcon = styled(CloseIcon)({
  cursor: 'pointer',
  width: '20px',
})

const alertStyles = {
  error: {
    backgroundColor: 'error.dark',
    icon: <ErrorOutlined />,
    defaultTitle: 'modalDialog.errorTitle',
  },
  warning: {
    backgroundColor: 'warning.dark',
    icon: <WarningAmber />,
    defaultTitle: 'modalDialog.warningTitle',
  },
  info: {
    backgroundColor: 'info.dark',
    icon: <InfoOutlined />,
    defaultTitle: 'modalDialog.infoTitle',
  },
}

interface AlertModalDialogProps extends PropsWithChildren {
  open: boolean
  onClose?: () => void
  maxWidth?: false | 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  fullWidth?: boolean
  withCloseIcon?: boolean
  title?: string
  minHeight?: number
  alert: 'error' | 'warning' | 'info'
}

export const AlertModalDialog = ({
  children,
  maxWidth = 'md',
  open,
  title,
  onClose,
  fullWidth = true,
  withCloseIcon = false,
  minHeight,
  alert,
}: AlertModalDialogProps): ReactElement => {
  const translate = useTranslate()

  const alertStyle = alertStyles[alert]

  return (
    <Dialog
      scroll="paper"
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      open={open}
      onClose={onClose}
      sx={
        minHeight
          ? {
              '& .MuiDialog-paper': {
                minHeight: 'calc(' + minHeight + '% - 64px)',
              },
            }
          : undefined
      }
    >
      <DialogTitle
        sx={{
          backgroundColor: alertStyle.backgroundColor,
          color: 'whitesmoke',
        }}
      >
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            {alertStyle.icon}
            <Typography
              color="whitesmoke"
              variant="h4"
              sx={{
                paddingLeft: 1,
              }}
            >
              {title ?? translate(alertStyle.defaultTitle)}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {withCloseIcon && <StyledCloseIcon onClick={onClose} />}
          </Box>
        </Stack>
      </DialogTitle>
      {children}
    </Dialog>
  )
}
